import React, { useEffect } from 'react';
import { string } from 'prop-types';

export const AVIS_VERIFIES_WIDGET_ID = 'da2c1387-d650-4e80-9cf6-945bf9ee56a1';
export const AVIS_VERIFIES_SCRIPT_SRC =
  'https://widgets.rr.skeepers.io/generated/03c78e48-c424-42d7-8037-773a37af650b/da2c1387-d650-4e80-9cf6-945bf9ee56a1.js';

const AVIS_VERIFIES_MAX_RETRIES = 2;
const AVIS_VERIFIES_RETRY_DELAY_MS = 1500;

let globalErrorSuppressionInstalled = false;

const stringifyUnhandledReason = reason => {
  if (reason == null) {
    return '';
  }
  if (typeof reason === 'string') {
    return reason;
  }
  if (typeof reason === 'object') {
    const parts = [reason.message, reason.name, reason.stack];
    let depth = 0;
    let c = reason.cause;
    while (c && depth < 5) {
      if (typeof c === 'string') {
        parts.push(c);
      } else if (c && typeof c === 'object') {
        parts.push(c.message, c.stack);
      }
      c = c.cause;
      depth += 1;
    }
    if (typeof AggregateError !== 'undefined' && reason instanceof AggregateError && Array.isArray(reason.errors)) {
      reason.errors.forEach(e => {
        parts.push(stringifyUnhandledReason(e));
      });
    }
    return parts.filter(Boolean).join(' ');
  }
  return String(reason);
};

/**
 * Rejette le bruit console / overlay (CRA, extensions type frame_ant) sur les fetch Skeepers qui échouent.
 */
export const shouldIgnoreAvisVerifiesRuntimeError = payload => {
  const lowerText = String(payload || '').toLowerCase();
  const fetchLike =
    lowerText.includes('failed to fetch') ||
    lowerText.includes('networkerror when attempting to fetch') ||
    lowerText.includes('networkerror') ||
    lowerText.includes('load failed');
  if (!fetchLike) {
    return false;
  }
  const hasSkeepersTrace =
    lowerText.includes('widgets.rr.skeepers.io') ||
    lowerText.includes('skeepers.io') ||
    lowerText.includes('da2c1387-d650-4e80-9cf6-945bf9ee56a1') ||
    lowerText.includes('ee86d9b9-b044-47be-967e-8aa10f69d230');
  const hasFrameAntTrace = lowerText.includes('frame_ant');
  /* Comme l’ancien footer : fetch en échec + (trace Skeepers ou extension frame_ant qui intercepte fetch). */
  return hasSkeepersTrace || hasFrameAntTrace;
};

/**
 * Enregistre une seule fois les écouteurs (phase capture) pour éviter l’overlay d’erreur sur fetch Skeepers
 * bloqués par une extension ou le réseau. À appeler le plus tôt possible côté client (ex. `index.js`).
 */
export const installAvisVerifiesSkeepersGlobalErrorSuppression = () => {
  if (typeof window === 'undefined' || globalErrorSuppressionInstalled) {
    return;
  }
  /* Installé très tôt depuis `public/index.html` pour passer avant react-error-overlay (dev). */
  if (window.__NEOKASA_SKEEPERS_FETCH_SUPPRESSION__) {
    globalErrorSuppressionInstalled = true;
    return;
  }
  globalErrorSuppressionInstalled = true;

  const handleGlobalError = event => {
    const signature = [
      event?.message,
      event?.filename,
      event?.error?.message,
      event?.error?.stack,
    ]
      .filter(Boolean)
      .join(' ');
    if (!shouldIgnoreAvisVerifiesRuntimeError(signature)) {
      return;
    }
    if (typeof event.preventDefault === 'function') {
      event.preventDefault();
    }
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }
  };

  const handleUnhandledRejection = event => {
    const signature = stringifyUnhandledReason(event?.reason);
    if (!shouldIgnoreAvisVerifiesRuntimeError(signature)) {
      return;
    }
    if (typeof event.preventDefault === 'function') {
      event.preventDefault();
    }
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }
  };

  window.addEventListener('error', handleGlobalError, true);
  window.addEventListener('unhandledrejection', handleUnhandledRejection, true);
};

/**
 * Charge le script Skeepers une fois (logique identique à l’ancien footer).
 * À utiliser une seule fois dans l’app (ex. `FooterContainer`).
 */
export const useAvisVerifiesSkeepersScript = () => {
  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    installAvisVerifiesSkeepersGlobalErrorSuppression();

    let disposed = false;
    let retryTimeoutId = null;
    let retries = 0;

    const hasWidgetContent = () => {
      const widgetRoot = document.getElementById(AVIS_VERIFIES_WIDGET_ID);
      if (!widgetRoot) {
        return false;
      }
      if (widgetRoot.querySelector('iframe')) {
        return true;
      }
      if (widgetRoot.childNodes && widgetRoot.childNodes.length > 0) {
        return true;
      }
      return Boolean(widgetRoot.innerHTML && widgetRoot.innerHTML.trim().length > 0);
    };

    const clearRetryTimeout = () => {
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
        retryTimeoutId = null;
      }
    };

    const scheduleRetry = () => {
      if (disposed || retries >= AVIS_VERIFIES_MAX_RETRIES) {
        return;
      }
      clearRetryTimeout();
      retries += 1;
      retryTimeoutId = setTimeout(() => {
        if (!disposed) {
          loadScript(true);
        }
      }, AVIS_VERIFIES_RETRY_DELAY_MS);
    };

    const loadScript = forceReload => {
      if (disposed) {
        return;
      }

      let script = document.querySelector(`script[src="${AVIS_VERIFIES_SCRIPT_SRC}"]`);
      if (forceReload && script) {
        script.remove();
        script = null;
      }

      if (!script) {
        script = document.createElement('script');
        script.src = AVIS_VERIFIES_SCRIPT_SRC;
        script.async = true;
        document.body.appendChild(script);
      }

      script.onload = () => {
        script.dataset.neokasaAvisVerifiesLoaded = 'true';
        if (!hasWidgetContent()) {
          scheduleRetry();
        }
      };
      script.onerror = () => {
        scheduleRetry();
      };

      if (script.dataset.neokasaAvisVerifiesLoaded === 'true' && !hasWidgetContent()) {
        scheduleRetry();
      }
    };

    loadScript(false);

    return () => {
      disposed = true;
      clearRetryTimeout();
    };
  }, []);
};

/**
 * Conteneur attendu par le script Skeepers (`id` fixe imposé par le tableau de bord).
 */
export const SkeepersAvisVerifiesWidgetMount = props => {
  const { className, ariaLabel = 'Avis clients vérifiés' } = props;
  return (
    <div className={className} aria-label={ariaLabel}>
      <div id={AVIS_VERIFIES_WIDGET_ID} />
    </div>
  );
};

SkeepersAvisVerifiesWidgetMount.propTypes = {
  className: string,
  ariaLabel: string,
};
